<template>
  <main class="page-operative page-operative-balance-balance d-flex flex-column h-100">
    <div class="flex-grow-1 container-fluid d-flex flex-column h-100">
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-md-nowrap mb-5">
        <PageTitle class="mb-2 mb-md-0">Stato Patrimoniale Finanziario</PageTitle>

        <div class="actions">
          <button class="btn btn-primary d-inline mr-2" @click.prevent="toggleShow">
            <template v-if="show === 'month'">Visualizza per annualità</template>
            <template v-else>Visualizza per mensilità</template>
          </button>
          <b-dropdown id="download-dropdown" class="d-inline" :no-caret="true" variant="outline-primary">
            <template v-slot:button-content>
              <i class="fas fa-ellipsis-v mr-2"></i>
              Stampa
            </template>
            <b-dropdown-item @click="printSPF">Stampa SPF</b-dropdown-item>
            <b-dropdown-item @click="printBusinessPlan">Stampa business plan</b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <div class="flex-grow-1 d-flex flex-column h-100 overflow-auto">
        <LoadingDataLoader v-if="isLoadingData"></LoadingDataLoader>
        <ErrorOnLoadingDataFeedback v-else-if="errorLoadingData"></ErrorOnLoadingDataFeedback>
        <template v-else-if="show === 'month'">
          <div class="overflow-auto report-texts-size">
            <OperativeTable dense sticky>
              <tbody>
              <OperativeTableBodyHeader :years="monthsTableHeadings" show-years>Impieghi</OperativeTableBodyHeader>
              <OperativeTableBodyRow :values="monthsTableRows.attivoImmobilizzato">
                Attivo immobilizzato
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.scorte">
                Scorte
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.clienti">
                Clienti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.creditiTributari">
                Crediti tributari
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow v-if="isModuleSubsidizedFinanceEnabled" :values="monthsTableRows.creditiContributiDaErogare">
                Crediti per contributi da erogare
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.creditoIva">
                Crediti IVA
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.altriCrediti">
                Altri crediti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.creditoImposta">
                Credito d'imposta
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.liquiditaImmediate">
                Liquidità immediate
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.riservaRettificaImpieghi" em>
                Riserva di rettifica
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.totaleCapitaleInvestito" strong>
                Totale capitale investito
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>

              <OperativeTableBodyHeader :years="monthsTableHeadings" show-years>Fonti</OperativeTableBodyHeader>
              <OperativeTableBodyRow :values="monthsTableRows.patrimonioNetto" strong>
                Patrimonio netto
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.equity" strong>
                Equity
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.capitaleSociale">
                Capitale sociale
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.prestitoSoci">
                Prestito soci
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.riserveUtili">
                Riserve di utili
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.riserve">
                Altre riserve
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.utiliPerditePregresse">
                Utile/Perdita annuale pregressa cumulata
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.utilePerdita">
                Utile/Perdita mensile
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.passivitaConsolidate" strong>
                Passività consolidate
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.finanziamentiMLTermine">
                Finanziamenti a Medio/Lungo Termine
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.fondi">
                Fondi
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.passivitaCorrenti" strong>
                Passività correnti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.bancheBreve">
                Banche a breve
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.rateMLTermineScadenza">
                Rate Medio/Lungo Termine in scadenza
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.fornitori">
                Fornitori
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.debitoIva">
                Debito IVA
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.debitiTributariImposte">
                Debiti Tributari per Imposte
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.debitiTributariRitenute">
                Debiti Tributari per Ritenute
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.debitiEntiPrevidenziali">
                Debiti verso Enti Previdenziali
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.altriDebiti">
                Altri debiti
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.riscontiPassivi">
                Risconti passivi
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.riservaRettificaFonti" em>
                Riserva di rettifica
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyRow :values="monthsTableRows.totaleCapitaleAcquisito" strong>
                Totale capitale acquisito
                <template #cell-content="{ value }">{{ value | money }}</template>
              </OperativeTableBodyRow>
              <OperativeTableBodyHeader :years="monthsTableHeadings" show-years></OperativeTableBodyHeader>
              </tbody>
            </OperativeTable>
          </div>
        </template>
        <template v-else>
          <AnnualSPFTable
            class="report-texts-size"
            dense
            :isModuleSubsidizedFinanceEnabled="isModuleSubsidizedFinanceEnabled"
            :yearsTableHeadings="yearsTableHeadings"
            :yearsTableRows="yearsTableRows"
          />
        </template>
      </div>
    </div>
  </main>
</template>

<script>

import operativePageMixin from '@/mixins/operativePage.js';
import loadingDataMixin from '@/mixins/loadingData.js';
import computedFinancialDataMixin from '@/mixins/computedFinancialData.js';
import formatMoneyFilter from '@/filters/formatMoney.js';
import { companyHasModule } from '@/helpers/modules.js';

import { BDropdown, BDropdownItem } from 'bootstrap-vue';

export default {
  mixins: [operativePageMixin, loadingDataMixin, computedFinancialDataMixin],
  components: {
    PageTitle: () => import('@/views/components/Layout/LayoutOperative/PageTitle.vue'),
    ErrorOnLoadingDataFeedback: () => import('@/views/components/Operative/ErrorOnLoadingDataFeedback.vue'),
    LoadingDataLoader: () => import('@/views/components/Operative/LoadingDataLoader.vue'),
    OperativeTable: () => import('@/views/components/Operative/OperativeTable.vue'),
    OperativeTableBodyHeader: () => import('@/views/components/Operative/OperativeTableBodyHeader.vue'),
    OperativeTableBodyRow: () => import('@/views/components/Operative/OperativeTableBodyRow.vue'),
    AnnualSPFTable: () => import('@/views/components/Operative/OperativeTable/AnnualSPFTable.vue'),
    BDropdown,
    BDropdownItem,
  },
  filters: {
    money: formatMoneyFilter,
  },
  data () {
    return {
      show: 'year',
      years: [],
      months: [],
    };
  },
  computed: {
    isModuleSubsidizedFinanceEnabled () {
      return companyHasModule(this.company, 'SUBSIDIZED_FINANCE');
    },
    monthsTableHeadings () {
      return this.convertToMonthsHeading(this.months);
    },
    monthsTableRows () {
      return this.convertToRowGroupAutomatically(this.months);
    },
    yearsTableHeadings () {
      return this.convertToYearsHeading(this.years);
    },
    yearsTableRows () {
      return this.convertToRowGroupAutomatically(this.years);
    },
  },
  methods: {
    retrieveData () {
      if (!this.company) {
        return;
      }

      this.resetErrorOnLoadingData();
      this.setDataLoading();

      if (this.show === 'month') {
        this.$api.getComputedStatoPatrimonialeFinanziarioMensile(this.company?.id)
          .then(res => {
            this.months = res?.data?.months || [];
          })
          .catch(this.setErrorOnLoadingData)
          .finally(this.setDataLoaded)
        ;

        return;
      }

      this.$api.getComputedStatoPatrimonialeFinanziario(this.company?.id)
        .then(res => {
          this.years = res?.data?.years || [];
        })
        .catch(this.setErrorOnLoadingData)
        .finally(this.setDataLoaded)
      ;
    },
    toggleShow () {
      if (this.show === 'month') {
        this.show = 'year';
      } else {
        this.show = 'month';
      }

      this.retrieveData();
    },
    printSPF () {
      this.$router.push({
        name: 'operative-company.balance.print',
        params: {
          section: 'SPF',
        },
        query: {
          forecast: this.$route.query?.forecast || null,
        },
      });
    },
    printBusinessPlan () {
      this.$router.push({
        name: 'operative-company.balance.print',
        params: {
          section: 'businessPlan',
        },
        query: {
          forecast: this.$route.query?.forecast || null,
        },
      });
    },
  },
  watch: {
    company () {
      this.retrieveData();
    },
  },
};

</script>
